<script setup lang="ts">
import type { MenuItem } from "primevue/menuitem";

import { useElementBounding } from "@vueuse/core";
import { computed, ref, watch } from "vue";

import {
  TRangeClickEvent,
  IInsertRange,
  TSelectedRange,
  TDuplicateDayProgramEvent,
  TEditRangeFormParams,
  TInsertRangeFormParams,
} from "@/components/programming/weekly-program/daily-program.type";
import {
  PROGRAM_WEEKEND_DAYS,
  PROGRAM_WORKING_DAYS,
} from "@/components/programming/weekly-program/duplicate-day-program";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { EProgramApplianceCategory } from "@/domains/v4b/program/program.types";
import { WeeklySubprogram } from "@/domains/v4b/program/subprogram.types";
import { useTypedIntl } from "@/locales/typed-intl";
import { EDay } from "@/utils/date";

import ContextMenu from "primevue/contextmenu";

import DailyProgram from "@/components/programming/weekly-program/daily-program.vue";
import DuplicateDayProgramDialog from "@/components/programming/weekly-program/duplicate-day-program-dialog.vue";
import ProgramFormDialog from "@/components/programming/weekly-program/program-form-dialog.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import ConfirmationDialog from "@/components/ui/dialog/confirmation-dialog.vue";

interface IProps {
  applianceCategory: EProgramApplianceCategory;
  weeklyProgram?: WeeklySubprogram;
  editable?: boolean;
  duplicateDay: (params: TDuplicateDayProgramEvent) => Promise<boolean>;
  duplicateDayLoading?: boolean;
}

interface IEmits {
  (e: "delete", params: TSelectedRange): void;
  (e: "edit", params: TEditRangeFormParams): void;
  (e: "insert", params: TInsertRangeFormParams): void;
}

const props = withDefaults(defineProps<IProps>(), {
  editable: true,
  duplicateDayLoading: false,
});
const emits = defineEmits<IEmits>();

const hourScaleElement = ref<HTMLElement | null>(null);
const rangeContextMenu = ref();

const isProgramFormDialogVisible = ref<boolean>(false);
const isDeleteProgramDialogVisible = ref<boolean>(false);
const isDuplicateDayProgramDialogVisible = ref<boolean>(false);
const programFormDialogMode = ref<"edit" | "create">();

const selectedRange = ref<TSelectedRange | IInsertRange | null>(null);
const selectedDay = ref<EDay | null>(null);

const { t, k } = useTypedIntl();
const { width: containerWidth } = useElementBounding(hourScaleElement);

const hourLabels = Array.from({ length: 24 }, (_value, hour) => `${String(hour).padStart(2, "0")}H`);
const dayGroups = computed(() => [
  {
    id: "business",
    label: t(k.control.programming.details.businessDays),
    labelTestId: DATA_TEST_IDS.components.weeklyProgram.businessDaysLabel,
    days: PROGRAM_WORKING_DAYS,
  },
  {
    id: "weekend",
    label: t(k.control.programming.details.weekend),
    labelTestId: DATA_TEST_IDS.components.weeklyProgram.weekendLabel,
    days: PROGRAM_WEEKEND_DAYS,
  },
]);

watch(
  () => props.editable,
  (editable) => {
    if (editable) return;

    selectedRange.value = null;
    selectedDay.value = null;
    isProgramFormDialogVisible.value = false;
    isDeleteProgramDialogVisible.value = false;
    isDuplicateDayProgramDialogVisible.value = false;
    programFormDialogMode.value = undefined;
  },
);

watch(
  () => props.editable,
  (editable) => {
    if (editable) return;

    selectedRange.value = null;
    selectedDay.value = null;
    isProgramFormDialogVisible.value = false;
    isDeleteProgramDialogVisible.value = false;
    programFormDialogMode.value = undefined;
  },
);

const rangeContextMenuItems = ref<MenuItem[]>([
  {
    label: t(k.global.misc.add),
    icon: "pi pi-plus",
    command: () => {
      if (!props.editable) return;
      programFormDialogMode.value = "create";
      isProgramFormDialogVisible.value = true;
    },
  },
  {
    label: t(k.global.misc.edit),
    icon: "pi pi-pencil",
    command: () => {
      if (!props.editable) return;
      programFormDialogMode.value = "edit";
      isProgramFormDialogVisible.value = true;
    },
  },
  {
    separator: true,
  },
  {
    label: t(k.global.misc.delete),
    icon: "pi pi-trash",
    command: () => {
      if (!props.editable) return;
      isDeleteProgramDialogVisible.value = true;
    },
  },
]);

const confirmDeletionMessage = computed<string>(() => {
  const selected = selectedRange.value;

  if (!selected) return t(k.global.misc.confirm);

  return t(
    k.control.programming.range.deletion.confirmationMessage({
      mode: t(k.global.program.metaMode[selected.range.metaMode]),
      day: t(k.global.weekDays[selected.day]),
      startTime: selected.range.startTime,
      endTime: selected.range.endTime,
    }),
  );
});

function handleRangeClick(e: TRangeClickEvent) {
  if (!props.editable) return;

  rangeContextMenu.value?.show(e.event);
  selectedRange.value = {
    ...e.selectedRange,
  };
}

function handleOpenDuplicateDayProgramDialog(day: EDay) {
  if (!props.editable) return;

  hideRangeContextMenu();
  selectedDay.value = day;
  isDuplicateDayProgramDialogVisible.value = true;
}

function handleInsertProgram(e: IInsertRange) {
  if (!props.editable) return;

  hideRangeContextMenu();

  programFormDialogMode.value = "create";
  isProgramFormDialogVisible.value = true;

  selectedRange.value = {
    ...e,
  };
}

function handleDeleteRangeCallback() {
  if (!props.editable) return;

  isDeleteProgramDialogVisible.value = false;
  emits("delete", selectedRange.value!);
}

function handleEditRange(range: TEditRangeFormParams) {
  if (!props.editable) return;

  emits("edit", range);
}

function handleSubmitInsert(range: TInsertRangeFormParams) {
  if (!props.editable) return;

  emits("insert", range);
}

function handleHideRangeDialog() {
  if (isProgramFormDialogVisible.value || isDeleteProgramDialogVisible.value) return;
  selectedRange.value = null;
  programFormDialogMode.value = undefined;
}

function hideRangeContextMenu() {
  if (selectedRange.value) {
    selectedRange.value = null;
    rangeContextMenu.value?.hide();
  }
}

async function handleDuplicateDayProgram(targetDays: EDay[]): Promise<boolean> {
  if (!props.editable || !selectedDay.value) return false;

  return props.duplicateDay({
    sourceDay: selectedDay.value,
    targetDays,
  });
}
</script>

<template>
  <div
    class="flex h-fit w-full min-w-[960px] flex-col bg-white"
    :data-test-id="DATA_TEST_IDS.components.weeklyProgram.component"
  >
    <template v-if="props.weeklyProgram">
      <div class="flex w-full items-start gap-4 pb-7">
        <div class="flex shrink-0 self-stretch flex-col gap-12 pt-[58px]">
          <section
            v-for="group in dayGroups"
            :key="`labels-${group.id}`"
            class="flex flex-col items-start gap-[18px]"
          >
            <p
              class="text-[10px] font-normal leading-[normal] uppercase tracking-[0.1em] text-text-secondary"
              :data-test-id="group.labelTestId"
            >
              {{ group.label }}
            </p>

            <div class="flex flex-col items-start gap-[39px] text-sm font-semibold leading-[0] text-text-primary">
              <div
                v-for="day in group.days"
                :key="`label-${day}`"
                class="w-20 leading-[normal]"
                :data-day-label-group="group.id"
              >
                {{ t(k.global.weekDays[day]) }}
              </div>
            </div>
          </section>
        </div>

        <div class="flex min-w-0 flex-1 flex-col items-start">
          <div
            ref="hourScaleElement"
            class="flex h-[54px] w-full min-w-0 items-center border-b border-neutral-light"
            :data-test-id="DATA_TEST_IDS.components.weeklyProgram.hourScale"
          >
            <div
              v-for="hourLabel in hourLabels"
              :key="hourLabel"
              class="flex min-w-0 flex-1 items-center justify-center px-3 py-[18px]"
            >
              <span
                class="whitespace-nowrap text-center text-[10px] font-normal uppercase tracking-[0.1em] text-text-primary"
                :data-test-id="DATA_TEST_IDS.components.weeklyProgram.hour"
                :data-hour="hourLabel"
              >
                {{ hourLabel }}
              </span>
            </div>
          </div>

          <div
            class="flex w-full flex-col items-start gap-[50px] pt-6"
            :data-test-id="DATA_TEST_IDS.components.weeklyProgram.schedule"
          >
            <div
              v-for="group in dayGroups"
              :key="`programs-${group.id}`"
              class="flex w-full flex-col gap-2"
            >
              <DailyProgram
                v-for="day in group.days"
                :key="`program-${day}`"
                v-model="selectedRange"
                class="min-w-0 flex-1"
                :container-width="containerWidth"
                :daily-program="[day, props.weeklyProgram[day]]"
                :data-day="day"
                :data-day-program-group="group.id"
                :data-test-id="DATA_TEST_IDS.components.weeklyProgram.day"
                :editable="props.editable"
                @insert="handleInsertProgram($event)"
                @range-click="handleRangeClick($event)"
              />
            </div>
          </div>
        </div>

        <div
          v-if="props.editable"
          class="flex shrink-0 self-stretch flex-col items-start gap-[58px] pt-[82px]"
        >
          <div
            v-for="group in dayGroups"
            :key="`actions-${group.id}`"
            class="flex flex-col items-start gap-[16px]"
          >
            <HospitalityButton
              v-for="day in group.days"
              :key="`action-${day}`"
              :aria-label="t(k.control.programming.details.dayDuplication.title({ day: t(k.global.weekDays[day]) }))"
              :data-test-id="DATA_TEST_IDS.components.weeklyProgram.dailyProgramOptions.button"
              :data-day="day"
              is-icon-only
              severity="tertiary"
              size="xs"
              @click="handleOpenDuplicateDayProgramDialog(day)"
            >
              <svg
                aria-hidden="true"
                class="size-[16.5px] shrink-0 overflow-visible"
                :data-test-id="DATA_TEST_IDS.components.weeklyProgram.dailyProgramOptions.icon"
                fill="none"
                overflow="visible"
                viewBox="0 0 16.5 16.5"
              >
                <path
                  d="M3 10.5C2.30109 10.5 1.95163 10.5 1.67598 10.3858C1.30843 10.2336 1.01642 9.94157 0.864182 9.57403C0.750001 9.29837 0.750001 8.94891 0.750001 8.25V3.15C0.750001 2.30992 0.750001 1.88988 0.913492 1.56902C1.0573 1.28677 1.28677 1.0573 1.56902 0.913492C1.88988 0.750001 2.30992 0.750001 3.15 0.750001H8.25C8.94891 0.750001 9.29837 0.750001 9.57403 0.864182C9.94157 1.01642 10.2336 1.30843 10.3858 1.67598C10.5 1.95163 10.5 2.30109 10.5 3M8.4 15.75H13.35C14.1901 15.75 14.6101 15.75 14.931 15.5865C15.2132 15.4427 15.4427 15.2132 15.5865 14.931C15.75 14.6101 15.75 14.1901 15.75 13.35V8.4C15.75 7.55992 15.75 7.13988 15.5865 6.81902C15.4427 6.53677 15.2132 6.3073 14.931 6.16349C14.6101 6 14.1901 6 13.35 6H8.4C7.55992 6 7.13988 6 6.81902 6.16349C6.53677 6.3073 6.3073 6.53677 6.16349 6.81902C6 7.13988 6 7.55992 6 8.4V13.35C6 14.1901 6 14.6101 6.16349 14.931C6.3073 15.2132 6.53677 15.4427 6.81902 15.5865C7.13988 15.75 7.55992 15.75 8.4 15.75Z"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                />
              </svg>
            </HospitalityButton>
          </div>
        </div>
      </div>
    </template>

    <div v-else>
      <span>{{ t(k.global.noData) }}</span>
    </div>
  </div>

  <ContextMenu
    v-if="props.editable"
    ref="rangeContextMenu"
    :data-test-id="DATA_TEST_IDS.components.weeklyProgram.contextMenu"
    :model="rangeContextMenuItems"
    @hide="handleHideRangeDialog()"
  />

  <ProgramFormDialog
    v-if="props.editable"
    v-model="isProgramFormDialogVisible"
    :appliance-category="props.applianceCategory"
    :mode="programFormDialogMode"
    :selected-range="selectedRange"
    @edit="handleEditRange($event)"
    @hide="handleHideRangeDialog()"
    @insert="handleSubmitInsert($event)"
  />

  <ConfirmationDialog
    v-if="props.editable"
    v-model="isDeleteProgramDialogVisible"
    :callback="() => handleDeleteRangeCallback()"
    delete-mode
    :subtitle="confirmDeletionMessage"
    :title="t(k.control.programming.range.deletion.title)"
    @hide="handleHideRangeDialog()"
  />

  <DuplicateDayProgramDialog
    v-if="props.editable && selectedDay"
    v-model="isDuplicateDayProgramDialogVisible"
    :loading="props.duplicateDayLoading"
    :source-day="selectedDay"
    :submit-action="handleDuplicateDayProgram"
  />
</template>
